'use client';

import { Children, type ReactNode } from 'react';
import { useStageInsetHost, useStageInsets } from './useStageInsets';

/**
 * The bars under the map, floating over it instead of pushing it up.
 *
 * Five surfaces share the bottom of the COP: the manual refusal note, the
 * FLY TO HERE confirm bar, the fly-to outcome note, the GO-TO altitude bar and
 * the command bar. In the classic shell each is its own row in the map's flex
 * column, so every one of them appearing or disappearing shortens the map and
 * fires `map.resize()` (`DroneMap.tsx:781`) - which moves the aircraft the
 * operator is tracking, mid-decision, at the exact moment they are arming a
 * command at it.
 *
 * This host takes the same five components, unchanged, and puts them over the
 * map's bottom edge. It does not know what any of them says or sends: it is a
 * position, not a policy.
 */

/**
 * Widest the stack may draw, in CSS px.
 *
 * Wide enough for the command bar's full key row on a laptop; narrow enough that
 * a 2560 px display does not get a bar stretched across two feet of glass, which
 * puts the emergency keys an arm's length from the ones next to them.
 */
export const BOTTOM_STACK_MAX_WIDTH_PX = 880;

/**
 * Kept clear at each bottom corner, in CSS px.
 *
 * Not a margin for looks. On a touch pointer `manualControlAnchor` puts the
 * flight control in the handed BOTTOM CORNER, because a two-handed grip on a
 * tablet makes the middle of the screen the least reachable part of it. A bar
 * that ran corner to corner would sit on top of the grab halo - the one gesture
 * the whole manual design protects. So the stack is centred and never enters
 * either corner, and `useStageInsetHost` reports its height so the ring is drawn
 * above it rather than under it.
 */
export const BOTTOM_STACK_CORNER_CLEARANCE_PX = 96;

export interface CopBottomStackProps {
  /**
   * Whether the stack is currently floating over the map, for the REGISTRY only.
   *
   * Deliberately not a class switch. Whether the bars float is decided in CSS by
   * the `lg:` variants below, so that crossing the breakpoint restyles one DOM
   * tree instead of re-parenting the bars into a different one. This flag says
   * the same thing in JS, and its only job is to keep `useStageInsetHost` from
   * claiming an inset below `lg`, where the stack is in the map's flow and
   * covers nothing - reporting a height there would shrink the measured surface
   * twice, once by the layout and once by the inset, and refuse manual flight
   * that is available.
   *
   * Below `lg` the details panel is a `fixed z-40` drawer and these bars beat it
   * with `z-50` on purpose (`page.tsx`, the comment above the bars): the defect
   * that records is a GO-TO altitude bar behind the drawer sending a leg out at
   * an altitude nobody saw.
   */
  floating: boolean;
  children: ReactNode;
}

export function CopBottomStack({ floating, children }: CopBottomStackProps) {
  // `Children.toArray` drops the `false` that every one of these bars renders as
  // when its condition is unmet. Nothing selected means no bars, and an empty
  // host would still be 12 px of bottom padding - a phantom inset that shrinks
  // the manual control surface to make room for nothing.
  const hasBars = Children.toArray(children).length > 0;

  // Registered only while it actually covers the map. In flow it covers nothing,
  // and reporting a height there would shrink the measured surface twice - once
  // by the layout, once by the inset - and refuse manual flight that is available.
  const ref = useStageInsetHost<HTMLDivElement>('bottom', floating && hasBars);

  // Centred on the map the operator can SEE, not on the canvas.
  //
  // The canvas runs under the floating side panel, so centring on it puts the
  // bar off-centre by half the panel's width at every screen size, and on a
  // 1366 px laptop with the panel open it puts about 109 px of bar over the
  // panel - at `z-30` over its `z-20`, so the bar wins and draws on top of it.
  // The end that overlaps is the tail of the key row: RTH, LAND and DISARM.
  //
  // Both horizontal edges, though only the right one has a publisher today. The
  // rail floats next (slice 9) and publishes `left`, and a bar centred on a map
  // whose left edge has moved is the same defect mirrored.
  //
  // Padding rather than a narrower box: the corner clearance below is what keeps
  // the stack out of the bottom corners where the touch flight control anchors,
  // and those corners belong to the visible map too.
  const insets = useStageInsets();
  const horizontalPadding = floating
    ? {
        ...(insets.left > 0 ? { paddingLeft: `calc(0.75rem + ${insets.left}px)` } : null),
        ...(insets.right > 0 ? { paddingRight: `calc(0.75rem + ${insets.right}px)` } : null),
      }
    : undefined;

  // ONE tree at every width. `display: contents` below `lg` makes both wrappers
  // generate no box at all, so the bars are the same flex children of the map
  // column they have always been - and crossing the breakpoint restyles this
  // tree rather than swapping it for another one.
  //
  // Returning a bare fragment below `lg` was the obvious way to write this and
  // it is wrong: React re-parents the children when the element at this position
  // changes shape, which unmounts every bar. `DroneCommandBar` holds the armed
  // key awaiting its second press, the typed takeoff altitude and the flag
  // saying the operator set it by hand; all three reset. Browser zoom crosses
  // `lg` (Ctrl+= on a 1280 px window), so this is reachable without touching the
  // window - and it reverts a hand-set takeoff altitude to the org default with
  // nothing on screen to say so. `ManualStage`'s own header states the rule this
  // follows: one DOM tree, because re-parenting destroys what is in it.
  return (
    <div
      ref={ref}
      data-testid="cop-bottom-stack"
      data-floating={floating && hasBars ? 'true' : 'false'}
      // `lg:bottom-0` with the gap as PADDING, not `bottom-3`: the measured
      // height then includes the gap, so the inset describes the whole band the
      // stack owns rather than leaving a 12 px sliver of map the ring could
      // reach into and the operator could not use.
      className="contents lg:pointer-events-none lg:absolute lg:inset-x-0 lg:bottom-0 lg:z-30 lg:flex lg:flex-col lg:items-center lg:px-3 lg:pb-3"
      // Below `lg` this element generates no box, so the padding is inert there
      // and there is no second tree to keep in step.
      style={horizontalPadding}
    >
      <div
        // Inert, all the way down to each bar's own wrapper, which opts back in
        // with `pointer-events-auto` in `page.tsx`. Putting it here instead would
        // have made the 8 px gaps BETWEEN the bars swallow map clicks - and the
        // COP dispatches a GO-TO on the next map click with no dialog, so a
        // swallowed click is a command the operator issued and never sees land.
        // `DroneMapControls` documents the same trap for the bottom-left stack.
        className="contents lg:pointer-events-none lg:flex lg:w-full lg:flex-col lg:gap-2"
        style={{
          maxWidth: `min(${BOTTOM_STACK_MAX_WIDTH_PX}px, 100% - ${BOTTOM_STACK_CORNER_CLEARANCE_PX * 2}px)`,
          // The cap is a preference; this is the floor, and the floor wins - CSS
          // resolves `min-width` after `max-width`, so the column is never
          // narrower than its widest bar's own minimum.
          //
          // Without it the cap silently truncates the command bar. Its key group
          // is `shrink-0` and the bar has no wrap and no `overflow-hidden`, so a
          // cap below its min-content spills the tail outside the card's border
          // onto bare map - and the tail is RTH, LAND, CONFIRM LANDING and
          // DISARM. On `main` the bar was the map column's width and fit on a
          // wide display; the cap took that away at every width.
          //
          // Since #505 slice 10 the bar renders its PHASE rather than all twelve
          // keys, so the widest case is now the airborne one rather than every
          // state. That makes the floor easier to satisfy and no less necessary:
          // the widest bar is still wider than the corner clearance leaves, and
          // asking the browser rather than picking a number is exactly what
          // keeps this true as the key set changes.
          //
          // Asking the browser for `min-content` rather than picking a number
          // keeps this true when a key, a callsign or a refusal changes length -
          // a magic width would go stale the first time one did, and the way it
          // would fail is the emergency keys drawn over a moving map.
          minWidth: 'min-content',
        }}
      >
        {children}
      </div>
    </div>
  );
}
